import { observer } from "mobx-react";
import React from "react";
import {
  Button,
  Divider,
  Flex,
  HStack,
  Image,
  Link,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useStore } from "@/store";
import { translations } from "@/constants/translations";
import { OrderSummaryTable } from "../Summary/OrderSummaryTable";
import { renderInnerHtml } from "@/helpers/dom";
import { AppLink } from "@/routing/AppLink";
import { H1, H3 } from "@/components/Heading";
import { FormSummaryItem } from "@/components/Form/FormSummaryItem";
import { useEffectOnce } from "usehooks-ts";
import { OrderFeedbackPanel } from "./OrderFeedbackPanel";
import { telemetry } from "@/telemetry";

interface Props {
  children: React.ReactNode;
}

export const OrderConfirmedPanel = observer(({ children }: Props) => {
  const {
    activeLanguage,
    activeIndigenousCommunity,
    isIndigenousFlow,
    orderData,
    setOrderData,
    orderCreatedData,
    isEmergentFlow,
    shippingProvinceLabel,
  } = useStore();
  const language = translations.pages["order-confirm"][activeLanguage];
  const summaryLanguage = translations.pages["order-summary"][activeLanguage];
  const imageTranslations = translations.common.images[activeLanguage];

  const handlePrint = () => {
    telemetry.trackEvent("confirmation_printed", {
      order_id: orderCreatedData?.orderId,
    });
    window.print();
  };

  useEffectOnce(() => setOrderData({ ...orderData, isActive: false }));

  return (
    <Stack spacing="14">
      {activeIndigenousCommunity && (
        <H1 textAlign="center">{activeIndigenousCommunity.name}</H1>
      )}
      {children}

      <Stack spacing={{ base: 4, md: 14 }}>
        <H3>{language.order_summary}</H3>
        <OrderSummaryTable />
        {orderData.ada && (
          <>
            <HStack alignItems="center">
              <H3>
                {isIndigenousFlow
                  ? language.emergent_ada
                  : language.emergent_direct_account_ada}
              </H3>
              <Text>{orderData.ada}</Text>
            </HStack>
          </>
        )}
        {orderData.orderReference && (
          <HStack>
            <H3>{language.order_reference_number}</H3>
            <Text>{orderData.orderReference}</Text>
          </HStack>
        )}
        <Stack spacing="8">
          <H3>{language.shipping_address}</H3>
          <SimpleGrid
            columns={{ sm: 1, md: 2 }}
            spacing="6"
          >
            <FormSummaryItem
              label={isIndigenousFlow ? language.community_name : language.name}
              value={
                isIndigenousFlow
                  ? activeIndigenousCommunity?.name ?? ""
                  : orderData.shippingName
              }
            />
            {!isIndigenousFlow && (
              <FormSummaryItem
                label={language.business_name}
                value={orderData.shippingBusinessName}
              />
            )}
            <FormSummaryItem
              label={language.street_address}
              value={orderData.shippingStreetAddress}
            />
            <FormSummaryItem
              label={language.unit_suit}
              value={orderData.shippingOptionalUnit}
            />
            <FormSummaryItem
              label={language.city}
              value={orderData.shippingCity}
            />
            <FormSummaryItem
              label={
                isEmergentFlow
                  ? summaryLanguage.province_territory
                  : summaryLanguage.province
              }
              value={shippingProvinceLabel}
            />
            <FormSummaryItem
              label={language.postal_code}
              value={orderData.shippingPostalCode}
            />
          </SimpleGrid>
        </Stack>

        {isIndigenousFlow && (
          <Stack spacing="8">
            <H3>{language.contact_information}</H3>
            <SimpleGrid
              columns={{ sm: 1, md: 2 }}
              spacing="6"
            >
              <FormSummaryItem
                label={language.contact_name}
                value={orderData.shippingName}
              />
              <FormSummaryItem
                label={language.email_address}
                value={orderData.emailAddress}
              />
              <FormSummaryItem
                label={language.phone_no}
                value={orderData.phoneNumber}
              />
            </SimpleGrid>
          </Stack>
        )}
      </Stack>

      <Divider />

      <OrderFeedbackPanel orderId={orderCreatedData?.id ?? 0} />

      <SimpleGrid
        columns={{ base: 1, lg: 3 }}
        spacing="10"
      >
        <Stack
          alignItems="center"
          spacing="8"
        >
          <Flex
            flex="1"
            alignItems="center"
          >
            <Image
              src={language.brochure_img_url}
              alt="brochure"
              maxWidth="100%"
              maxHeight="360px"
            />
          </Flex>
          <Link
            href={language.brochure_pdf_link}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              whiteSpace="normal"
              wordBreak="break-word"
            >
              {renderInnerHtml(language.download_brochure)}
            </Button>
          </Link>
        </Stack>
        <Stack
          alignItems="center"
          spacing="8"
        >
          <Flex
            flex="1"
            alignItems="center"
          >
            <Image
              src={
                isIndigenousFlow
                  ? language.instructions_indigenous_img_url
                  : language.instructions_img_url
              }
              alt="instructions"
              maxWidth="100%"
              maxHeight="360px"
            />
          </Flex>

          <Link
            href={
              isIndigenousFlow
                ? language.instructions_indigenous_pdf_link
                : language.instructions_pdf_link
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              whiteSpace="normal"
              wordBreak="break-word"
            >
              {renderInnerHtml(language.download_instructions)}
            </Button>
          </Link>
        </Stack>

        <Stack
          alignSelf="center"
          alignItems="center"
          justifyContent="center"
          spacing="8"
          height="100%"
        >
          <Flex
            flex="1"
            alignItems="center"
          >
            <Image
              src={imageTranslations.training_video}
              alt="Training video preview"
              maxWidth="100%"
            />
          </Flex>
          <AppLink to="/training">
            <Button
              whiteSpace="normal"
              wordBreak="break-word"
            >
              {renderInnerHtml(language.video_access_label)}
            </Button>
          </AppLink>
        </Stack>
      </SimpleGrid>

      <Divider />

      <HStack
        justifyContent="space-between"
        flexDirection={{ base: "column", sm: "row" }}
      >
        <Button onClick={handlePrint}>{language.print_page}</Button>
        <AppLink to="/">
          <Button>{language.return_home}</Button>
        </AppLink>
      </HStack>
    </Stack>
  );
});
